import type { Types } from "mongoose";

export interface PageCheckoutLineItem {
  productId: string;
  variantId?: string;
}

export type PageOnCheckoutSuccessAction =
  | { type: "GoTo"; pageId: string }
  | { type: "StartAutomation"; automationId: string };

/** Select field for checkout /contact; options listed in order for the dropdown. */
export interface ContactCustomFieldDef {
  key: string;
  label: string;
  required?: boolean;
  options: string[];
}

export interface PageCheckoutSettings {
  contactCustomFields?: ContactCustomFieldDef[];
  paymentMethodTypes?: string[];
  /**
   * Stripe PaymentMethod Configuration id (`pmc_…`) for the PaymentIntent/Payment Element.
   * When set, this is used instead of `paymentMethodTypes` on create (Stripe excludes the two).
   */
  paymentMethodConfigurationId?: string;
  /** When true, hosted checkout collects a card via SetupIntent without charging. */
  savePaymentMethod?: boolean;
  /** Payment step submit button label; defaults to "Place Order". */
  submitBtnText?: string;
  /** Automation to start when this checkout is marked abandoned (per-page checkout flow). */
  onAbandonedAutomationId?: Types.ObjectId | string;
  /** Per-checkout tip prompt override (title/description only). */
  tipping?: {
    title?: string;
    description?: string;
  };
  /** Subject/body templates for Easyship shipment notice email (supports `{{FIRST_NAME}}`, `{{PARCEL_SECTIONS}}`, `{{{trackingDetails}}}` in html). */
  shipmentNotificationEmail?: {
    subject: string;
    html: string;
    /**
     * Handlebars snippet for parcel blocks (defaults to boxed package list + CTA when omitted).
     * Context: `{ parcels: { packageIndex, trackingUrl, productLabels[], packageSubtitle? }[] }`.
     */
    trackingDetails?: string;
  };
}

export interface PageOnSubmitActionType {
  type: "CreateLead" | "StartAutomation" | "GoTo" | "MailerLiteCreateSubscriber" | "Checkout";
  automationId?: string;
  pageId?: string;
  groupIds?: string[];
  lineItems?: PageCheckoutLineItem[];
  onCheckoutSuccess?: PageOnCheckoutSuccessAction[];
  /** When type is Checkout: snapshot onto the Checkout document (contact step select fields). */
  checkoutSettings?: PageCheckoutSettings;
}

export function normalizePageOnCheckoutSuccess(
  raw: PageOnCheckoutSuccessAction[] | PageOnCheckoutSuccessAction | undefined
): PageOnCheckoutSuccessAction[] {
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw;
  return [raw];
}
